import { ActionType, ComponentType, EventName } from './TelemetryEnums';
import { ITelemetryClient } from './interfaces/ITelemetryClient';
import * as Common from '@microsoft/applicationinsights-common';
import { ApplicationInsights, IConfiguration, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ActionName } from '../resources/TelemetryEvents';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { IEnvironmentValues } from '../models';
import { ScrubUpnInitializer } from './ScrubUpnInitializer';
import { UsageUserIdWithLogs } from '@careerhub/api';

export class IAppInsightsConfig {
  environmentName!: string;
  iKey!: string;
  serviceOffering!: string;
  serviceLine!: string;
  service!: string;
  componentName!: string;
  componentId!: string;
  history!: any;
}

export class AppInsightsClient implements ITelemetryClient {
  private readonly applicationInsights: ApplicationInsights;
  private readonly environmentValues: IEnvironmentValues;
  constructor(appInsightsSettings: IAppInsightsConfig) {
    var reactPlugin: ReactPlugin = new ReactPlugin();
    var aiConfig: IConfiguration & Common.IConfig = {
      instrumentationKey: appInsightsSettings.iKey,
      autoTrackPageVisitTime: true,
      enableAutoRouteTracking: true,
      enableRequestHeaderTracking: true,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: appInsightsSettings.history },
      },
      enableCorsCorrelation: true,
      disableCorrelationHeaders: false,
      maxAjaxCallsPerView: -1,
      isCookieUseDisabled: true,
      disableFetchTracking: false,
      disableAjaxTracking: false,
    };
    this.applicationInsights = new ApplicationInsights({
      config: aiConfig,
    });
    this.applicationInsights.loadAppInsights();
    this.applicationInsights.context.session.id;

    // ** Add Telemetry Initializers **
    this.applicationInsights.addTelemetryInitializer(ScrubUpnInitializer);

    // envDataInitializer
    this.environmentValues = {
      environmentName: appInsightsSettings.environmentName,
      serviceOffering: appInsightsSettings.serviceOffering,
      serviceLine: appInsightsSettings.serviceLine,
      service: appInsightsSettings.service,
      componentId: appInsightsSettings.componentId,
      componentName: appInsightsSettings.componentName,
    };

    var environmentDataInitializer = (envelope: ITelemetryItem) => {
      try {
        if (envelope && envelope.baseData) {
          envelope.baseData.properties = envelope.baseData.properties || {};
          envelope.baseData.properties['EnvironmentName'] = this.environmentValues.environmentName;
          envelope.baseData.properties['ServiceOffering'] = this.environmentValues.serviceOffering;
          envelope.baseData.properties['ServiceLine'] = this.environmentValues.serviceLine;
          envelope.baseData.properties['Service'] = this.environmentValues.service;
          envelope.baseData.properties['ComponentName'] = this.environmentValues.componentName;
          envelope.baseData.properties['ComponentId'] = this.environmentValues.componentId;
        }
      } catch (ex) {}
    };

    this.applicationInsights.addTelemetryInitializer(environmentDataInitializer);
  }

  public setUsageUserId(usageUserId: Promise<UsageUserIdWithLogs>) {
    // UserIdAnonymizer
    usageUserId.then((usageUserIdWithLogs) => {
      var anonymizeUserInitializer = (envelope: ITelemetryItem) => {
        try {
          if (envelope && envelope.baseData) {
            envelope.baseData.properties = envelope.baseData.properties || {};
            envelope.baseData.properties['UsageUserId'] = usageUserIdWithLogs.usageUserId;
          }
        } catch (ex) {
          this.logError(ex, {});
        }
      };
      this.applicationInsights.addTelemetryInitializer(anonymizeUserInitializer);
      if (usageUserIdWithLogs.errorLog) {
        this.logError(usageUserIdWithLogs.errorLog, {});
      }
    });
  }

  public logInfo(message: string, properties: {}) {
    this.applicationInsights.trackTrace({
      message: message,
      severityLevel: SeverityLevel.Information,
      properties: properties,
    });
  }

  public logWarning = (message: string, properties: {}) => {
    this.applicationInsights.trackTrace({
      message: message,
      severityLevel: SeverityLevel.Warning,
      properties: properties,
    });
  };

  public logError = (message: string, properties: {}) => {
    this.applicationInsights.trackTrace({
      message: message,
      severityLevel: SeverityLevel.Error,
      properties: properties,
    });
  };

  public logEvent = (eventName: string, properties: {} = {}, measurements: any = {}) => {
    this.applicationInsights.trackEvent({
      name: eventName,
      properties: properties,
      measurements: measurements,
    });
  };

  /**
   * Logs Button Clicks as Usage Events
   * @param featureName
   * @param subFeatureName
   */
  public logButtonClick = (featureName: string, subFeatureName: string, properties: any) => {
    var features = properties || {};
    features['FeatureName'] = featureName;
    features['SubFeatureName'] = subFeatureName;
    features['ActionType'] = 'User';
    features['ActionName'] = 'Click';
    features['EventName'] = 'ButtonClick';
    features['ComponentType'] = 'ComponentTypeWeb';
    this.applicationInsights.trackEvent({
      name: featureName, // TODO: Check this
      properties: features,
    });
  };

  /**
   * Logs Pin/Unpin Clicks as Usage Events
   * @param featureName
   * @param subFeatureName
   */
  public logPinUnpinChange = (featureName: string, subFeatureName: string, isPinned: boolean, properties: any) => {
    var actionName = isPinned == true ? ActionName.Pin : ActionName.UnPin;
    var features: any = properties || {};
    features['FeatureName'] = featureName;
    features['SubFeatureName'] = subFeatureName;
    features['ActionType'] = 'User';
    features['ActionName'] = actionName;
    features['EventName'] = 'ButtonClick';
    features['ComponentType'] = 'ComponentTypeWeb';
    this.applicationInsights.trackEvent({
      name: featureName, // TODO: Check this
      properties: features,
    });
  };

  /**
   * Logs CheckBox Change as Usage Event
   * @param featureName
   * @param subFeatureName
   */
  public logCheckBoxChange = (featureName: string, subFeatureName: string, ischecked: boolean = true) => {
    var actionName = ischecked == true ? ActionName.Check : ActionName.Uncheck;
    var features: any = {};
    features['FeatureName'] = featureName;
    features['SubFeatureName'] = subFeatureName;
    features['ActionType'] = 'User';
    features['ActionName'] = actionName;
    features['EventName'] = 'CheckboxChange';
    features['ComponentType'] = 'ComponentTypeWeb';
    this.applicationInsights.trackEvent({
      name: featureName, // TODO: Check this
      properties: features,
    });
  };

  /**
   * Logs Dropdown selected as Usage Event
   * @param featureName
   * @param subFeatureName
   * @param properties
   */
  public logDropDownSelected = (featureName: string, subFeatureName: string, properties: any = {}) => {
    properties['FeatureName'] = featureName;
    properties['SubFeatureName'] = subFeatureName;
    properties['ActionType'] = 'User';
    properties['ActionName'] = 'DropDownSelect';
    properties['EventName'] = 'DropDownSelected';
    properties['ComponentType'] = 'ComponentTypeWeb';
    this.applicationInsights.trackEvent({
      name: featureName, // TODO: Check this
      properties: properties,
    });
  };

  /**
   * Log feature usage event to see feature level HEART metrics
   * @param featureName
   * @param subFeatureName
   * @param eventName
   * @param actionName
   * @param actionType
   * @param componentType
   * @param properties
   * @param measurements
   */
  public logFeatureUsageEvent = (
    featureName: string,
    subFeatureName: string,
    eventName: EventName,
    actionName: ActionName,
    actionType: ActionType = ActionType.User,
    componentType: ComponentType = ComponentType.Web,
    properties: any = {},
    measurements: any = {}
  ) => {
    properties['FeatureName'] = featureName;
    properties['SubFeatureName'] = subFeatureName;
    properties['ActionType'] = actionType;
    properties['ActionName'] = actionName;
    properties['EventName'] = eventName;
    properties['ComponentType'] = componentType;
    this.applicationInsights.trackEvent({
      name: featureName, // TODO: Check this
      properties: properties,
      measurements: measurements,
    });
  };
}
